import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { SafeChatMarkdown } from "@/components/chat/ChatMarkdownBody";
import { ContinuityTensionSection } from "@/components/chat/ContinuityTensionSection";
import { buildContinuityTensions } from "@/lib/behavior/viewModels/buildContinuityTensions";
import type { CoachInsight } from "@/hooks/useCoachChat";
import type { HarnessPayload } from "@/types/harnessPayload";
import type { ContinuityReview } from "@/lib/behavior/coachContinuityReviewService";

interface CoachBriefDrawerProps {
  open: boolean;
  onClose: () => void;
  coachInsight: CoachInsight | null;
  harnessPayload?: HarnessPayload | null;
  carriedForwardReviews?: ContinuityReview[];
}

export function CoachBriefDrawer({ open, onClose, coachInsight, harnessPayload, carriedForwardReviews }: CoachBriefDrawerProps) {
  const [trailExpanded, setTrailExpanded] = useState(false);
  const tensions = useMemo(
    () => buildContinuityTensions(coachInsight, carriedForwardReviews ?? []),
    [coachInsight, carriedForwardReviews],
  );
  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent side="right" className="w-[22rem] p-0 sm:max-w-[22rem]">
        <div className="flex h-full flex-col">
          <SheetHeader className="border-b border-border px-5 py-4">
            <SheetTitle className="text-base">Coach Brief</SheetTitle>
          </SheetHeader>

          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {coachInsight ? (
              <>
                {/* Continuity tensions — coach only, rendered before situation.
                    Key on situation text so dismissed state resets on each new CoachInsight,
                    preventing stale dismissals from suppressing tensions in subsequent AI responses. */}
                <ContinuityTensionSection
                  tensions={tensions}
                  key={coachInsight.situation.slice(0, 80)}
                />

                {/* Situation + confidence */}
                <div className="rounded-lg border border-border bg-card p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Situation</p>
                    <Badge variant="outline" className="text-[10px]" title="Evidence confidence">
                      {coachInsight.confidence.toUpperCase()}
                    </Badge>
                  </div>
                  <SafeChatMarkdown
                    content={coachInsight.situation}
                    className="mt-1 text-sm prose prose-sm dark:prose-invert max-w-none prose-p:my-0.5 prose-ul:my-0.5 prose-li:my-0"
                  />
                </div>

                {/* Interpretation */}
                {coachInsight.interpretation && (
                  <div className="rounded-lg border border-border bg-card p-3">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Interpretation — coach only</p>
                    <SafeChatMarkdown
                      content={coachInsight.interpretation}
                      className="mt-1 text-sm prose prose-sm dark:prose-invert max-w-none prose-p:my-0.5 prose-ul:my-0.5 prose-li:my-0"
                    />
                  </div>
                )}

                {/* Evidence — linked to leader page when available */}
                {coachInsight.evidence.length > 0 && (
                  <div className="rounded-lg border border-border bg-card p-3">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Evidence</p>
                    <ul className="mt-1 space-y-1">
                      {coachInsight.evidence.map((item, i) => (
                        <li key={i} className="flex gap-2 text-sm">
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-violet-400" aria-hidden="true" />
                          {item.href ? (
                            <a href={item.href} className="hover:underline text-violet-400" target="_blank" rel="noopener noreferrer">
                              {item.text}
                              <ExternalLink className="inline h-3 w-3 ml-1" />
                            </a>
                          ) : (
                            <span>{item.text}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Coach move */}
                <div className="rounded-lg border border-border bg-card p-3">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Coach move</p>
                  <SafeChatMarkdown
                    content={coachInsight.coachMove}
                    className="mt-1 text-sm prose prose-sm dark:prose-invert max-w-none prose-p:my-0.5 prose-ul:my-0.5 prose-li:my-0"
                  />
                </div>

                {/* Veera's questions */}
                {coachInsight.questions && coachInsight.questions.length > 0 && (
                  <div className="rounded-lg border border-violet-500/20 bg-violet-500/5 p-3">
                    <p className="text-xs uppercase tracking-wide text-violet-400">Veera's questions</p>
                    <ul className="mt-1 space-y-1.5">
                      {coachInsight.questions.map((q, i) => (
                        <li key={i} className="text-sm italic text-foreground/90">"{q}"</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Watchout */}
                <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
                  <p className="text-xs uppercase tracking-wide text-amber-400">Watchout</p>
                  <SafeChatMarkdown
                    content={coachInsight.watchout}
                    className="mt-1 text-sm prose prose-sm dark:prose-invert max-w-none prose-p:my-0.5 prose-ul:my-0.5 prose-li:my-0"
                  />
                </div>

                {/* Missing data */}
                {coachInsight.missingData && coachInsight.missingData.length > 0 && (
                  <div className="rounded-lg border border-border bg-card p-3">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Missing data</p>
                    <ul className="mt-1 space-y-1">
                      {coachInsight.missingData.map((m, i) => (
                        <li key={i} className="flex gap-2 text-sm text-muted-foreground">
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/50" aria-hidden="true" />
                          <span>{m}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Explanation trail — harness transparency */}
                {harnessPayload && (
                  <div className="rounded-lg border border-border bg-card p-3">
                    <button
                      type="button"
                      className="flex w-full items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground hover:text-foreground"
                      onClick={() => setTrailExpanded((prev) => !prev)}
                    >
                      {trailExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                      Why Veera noticed this
                    </button>

                    {trailExpanded && (
                      <div className="mt-2 space-y-2">
                        <div className="flex flex-wrap gap-2">
                          <Badge variant="outline" className="text-[10px]">
                            {harnessPayload.approvedCount} approved
                          </Badge>
                          <Badge variant="outline" className="text-[10px]">
                            {harnessPayload.reviewRequiredCount} review
                          </Badge>
                          {harnessPayload.blockedCount > 0 && (
                            <Badge variant="outline" className="text-[10px] border-red-300 text-red-600 dark:border-red-500/40 dark:text-red-400">
                              {harnessPayload.blockedCount} blocked
                            </Badge>
                          )}
                        </div>

                        {harnessPayload.trails.map((trail) => (
                          <div key={trail.telemetryEventId} className="space-y-1 border-t border-border/50 pt-2">
                            <div className="flex items-center gap-1.5">
                              <Badge variant="secondary" className="text-[10px]">
                                {trail.policyMode}
                              </Badge>
                              <Badge
                                variant="outline"
                                className={
                                  trail.outcome === "surfaced"
                                    ? "text-[10px] border-green-300 text-green-700 dark:border-green-500/40 dark:text-green-400"
                                    : trail.outcome === "blocked"
                                      ? "text-[10px] border-red-300 text-red-600 dark:border-red-500/40 dark:text-red-400"
                                      : "text-[10px]"
                                }
                              >
                                {trail.outcome}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">{trail.decisionReason}</p>

                            {trail.steps.length > 0 && (
                              <ol className="mt-1 space-y-0.5 border-l-2 border-border pl-2.5">
                                {trail.steps.map((step, idx) => (
                                  <li key={idx} className="text-[11px] text-muted-foreground">{step}</li>
                                ))}
                              </ol>
                            )}

                            {trail.evidenceRefs.length > 0 && (
                              <div className="mt-1 flex flex-wrap gap-1">
                                {trail.evidenceRefs.map((ref, idx) => (
                                  <span key={idx} className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                                    {ref.source} ({ref.quality})
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                Brief becomes available after Veera returns an assistant insight for this context.
              </p>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
